import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "../store";
import { alertMessage } from "../actions/common";
import { errorHandler } from "app/shared/Service/errorHandler";
import { createQuery, getActiveConfig } from "app/utils/constants/common";
import {
  downloadConfirmedBoxesReportAPI,
  downloadDetailedDeliveryReportAPI,
  downloadExtraDaysReportAPI,
  downloadPaidClientReportAPI,
  downloadSalesReportAPI,
  downloadTopUpReportAPI,
  getClientBirthdayReportAPI,
  getClientReportsAPI,
  getDeliveryShiftCountReportAPI,
  getMealSalesReportAPI,
  getSubscriptionReportAPI,
  getUnconfirmedOrdersAutoSelectionsReportAPI,
} from "app/services/reports";
import { ReportInterace } from "app/types";
import { MealSalesReportPayload } from "app/types/meals";

interface InitialState {
  salesReportLoading: boolean;
  topUpReportLoading: boolean;
  isReportLoading: boolean;
  isSubscriptionListLoading: boolean;
  isExtraDaysReportLoading: boolean;
  IsMealSalesReportLoading: boolean;
  isClientBirthdayReportLoading: boolean;
}

const initialState: InitialState = {
  salesReportLoading: false,
  topUpReportLoading: false,
  isReportLoading: false,
  isSubscriptionListLoading: false,
  isExtraDaysReportLoading: false,
  IsMealSalesReportLoading: false,
  isClientBirthdayReportLoading: false,
};

export const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    setSalesReportLoading: (state, action: PayloadAction<boolean>) => {
      state.salesReportLoading = action.payload;
    },
    setTopUpReportLoading: (state, action: PayloadAction<boolean>) => {
      state.topUpReportLoading = action.payload;
    },
    setIsReportLoading: (state, action: PayloadAction<boolean>) => {
      state.isReportLoading = action.payload;
    },
    setSubscriptionListReportLoading: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isSubscriptionListLoading = action.payload;
    },
    setIsExtraDaysReportLoading: (state, action: PayloadAction<boolean>) => {
      state.isExtraDaysReportLoading = action.payload;
    },
    setIsMealSalesReportLoading: (state, action: PayloadAction<boolean>) => {
      state.IsMealSalesReportLoading = action.payload;
    },
    setIsClientBirthdayReportLoading: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isClientBirthdayReportLoading = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setSalesReportLoading,
  setTopUpReportLoading,
  setIsReportLoading,
  setSubscriptionListReportLoading,
  setIsExtraDaysReportLoading,
  setIsMealSalesReportLoading,
  setIsClientBirthdayReportLoading,
} = reportSlice.actions;

export default reportSlice.reducer;

interface SalesReportRequestPayload {
  fromDate: string;
  toDate: string;
  type: string;
}

export const downloadSalesReportAction = (
  payload: SalesReportRequestPayload
) => {
  return async (dispatch: AppDispatch): Promise<ReportInterace> => {
    try {
      const { storeId } = getActiveConfig();
      const query =
        "?" +
        createQuery({
          filter: payload,
        });
      dispatch(setSalesReportLoading(true));
      const {
        data: { data },
      } = await downloadSalesReportAPI(query, storeId);
      dispatch(setSalesReportLoading(false));
      return data;
    } catch (error) {
      dispatch(setSalesReportLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

interface ReportRequestPayload {
  fromDate: string;
  toDate: string;
}

export const downloadTopUpReportAction = (payload: ReportRequestPayload) => {
  return async (dispatch: AppDispatch): Promise<ReportInterace> => {
    const { storeId } = getActiveConfig();
    try {
      const query =
        "?" +
        createQuery({
          filter: payload,
        });
      dispatch(setTopUpReportLoading(true));
      const {
        data: { data },
      } = await downloadTopUpReportAPI(query, storeId);
      dispatch(setTopUpReportLoading(false));
      return data;
    } catch (error) {
      dispatch(setTopUpReportLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

interface DetailedDeliveryRequestPayload {
  fromDate: string;
  toDate: string;
}

export const downloadDetailedDeliveryReportAction = (
  payload: DetailedDeliveryRequestPayload
) => {
  return async (dispatch: AppDispatch) => {
    const { storeId } = getActiveConfig();
    try {
      const query =
        "?" +
        createQuery({
          filter: payload,
        });
      const {
        data: { data },
      } = await downloadDetailedDeliveryReportAPI(query, storeId);
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

interface PaidClientRequestPayload {
  fromDate: string;
  toDate: string;
}
export const downloadPaidClientReportAction = (
  paylaod: PaidClientRequestPayload
) => {
  return async (dispatch: AppDispatch): Promise<ReportInterace> => {
    const { storeId } = getActiveConfig();
    try {
      const query =
        "?" +
        createQuery({
          filter: paylaod,
        });
      const {
        data: { data },
      } = await downloadPaidClientReportAPI(query, storeId);
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

interface ConfirmedBoxesRequestPayload {
  fromDate: string;
  toDate: string;
}
export const downloadConfirmedBoxesReportAction = (
  paylaod: ConfirmedBoxesRequestPayload
) => {
  return async (dispatch: AppDispatch): Promise<ReportInterace> => {
    const { storeId } = getActiveConfig();
    try {
      const query =
        "?" +
        createQuery({
          filter: paylaod,
        });
      const {
        data: { data },
      } = await downloadConfirmedBoxesReportAPI(query, storeId);
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getClientReportsAction = ({
  storeId,
  payload,
}: {
  storeId: number;
  payload: {
    fromDate: string;
    toDate: string;
    type: string;
  };
}) => {
  return async (dispatch: AppDispatch): Promise<ReportInterace> => {
    try {
      const query =
        "?" +
        createQuery({
          filter: payload,
        });
      const { data } = await getClientReportsAPI({
        query,
        storeId,
      });
      const response = data.data;
      return response;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getUnconfirmedOrdersAutoSelectionReportAction = (
  payload: ReportRequestPayload
) => {
  return async (dispatch: AppDispatch): Promise<ReportInterace> => {
    const { storeId } = getActiveConfig();
    try {
      const query =
        "?" +
        createQuery({
          filter: payload,
        });
      dispatch(setIsReportLoading(true));
      const {
        data: { data },
      } = await getUnconfirmedOrdersAutoSelectionsReportAPI(query, storeId);
      dispatch(setIsReportLoading(false));
      return data;
    } catch (error) {
      dispatch(setIsReportLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getDeliveryShiftCountReportAction = (
  payload: ReportRequestPayload
) => {
  return async (dispatch: AppDispatch): Promise<ReportInterace> => {
    const { storeId } = getActiveConfig();
    try {
      const query =
        "?" +
        createQuery({
          filter: payload,
        });
      dispatch(setIsReportLoading(true));
      const {
        data: { data },
      } = await getDeliveryShiftCountReportAPI(query, storeId);
      dispatch(setIsReportLoading(false));
      return data;
    } catch (error) {
      dispatch(setIsReportLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};
interface ExtraDaysReportRequestPayload {
  fromDate: string;
  toDate: string;
  action: string;
}
export const getExtraDaysReportAction = (
  payload: ExtraDaysReportRequestPayload
) => {
  return async (dispatch: AppDispatch): Promise<ReportInterace> => {
    try {
      const { storeId } = getActiveConfig();

      const query =
        "?" +
        createQuery({
          filter: payload,
        });
      dispatch(setIsExtraDaysReportLoading(true));
      const {
        data: { data },
      } = await downloadExtraDaysReportAPI(query, storeId);
      dispatch(setIsExtraDaysReportLoading(false));
      return data;
    } catch (error) {
      dispatch(setIsExtraDaysReportLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

interface SubscriptionListReportPayload {
  startAtFrom?: string;
  startAtTo?: string;
  estimatedEndAtFrom?: string;
  estimatedEndAtTo?: string;
  statuses?: string[];
  gender?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  packageId?: number;
}
export const getSubscriptionListReportAction = (
  payload: SubscriptionListReportPayload
) => {
  return async (dispatch: AppDispatch): Promise<ReportInterace> => {
    try {
      const { storeId } = getActiveConfig();
      const query =
        "?" +
        createQuery({
          filter: payload,
        });
      dispatch(setSubscriptionListReportLoading(true));
      const {
        data: { data },
      } = await getSubscriptionReportAPI(query, storeId);
      dispatch(setSubscriptionListReportLoading(false));
      return data;
    } catch (error) {
      dispatch(setSubscriptionListReportLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getMealSalesReportAction = (payload: MealSalesReportPayload) => {
  return async (dispatch: AppDispatch): Promise<ReportInterace> => {
    try {
      const { storeId } = getActiveConfig();
      const query =
        "?" +
        createQuery({
          filter: payload,
        });
      dispatch(setIsMealSalesReportLoading(true));
      const {
        data: { data },
      } = await getMealSalesReportAPI(query, storeId);
      dispatch(setIsMealSalesReportLoading(false));
      return data;
    } catch (error) {
      dispatch(setIsMealSalesReportLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

interface ClientBirthdayReportPayload {
  fromDate: string;
  toDate: string;
  type: string;
  statuses: string[];
}

const clientBirthdayReportCustomFields = [
  {
    customKey: "fromDate",
    newValue: "birthDayFrom",
    valueType: "text" as const,
  },
  {
    customKey: "toDate",
    newValue: "birthDayTo",
    valueType: "text" as const,
  },
  {
    customKey: "statuses",
    newValue: "Statuses",
    valueType: "array" as const,
  },
];

export const getClientBirthdayReportAction = (
  payload: ClientBirthdayReportPayload
) => {
  return async (dispatch: AppDispatch): Promise<ReportInterace> => {
    try {
      const { storeId } = getActiveConfig();
      const query =
        "?" +
        createQuery({
          filter: payload,
          customFields: clientBirthdayReportCustomFields,
        });
      dispatch(setIsClientBirthdayReportLoading(true));
      const {
        data: { data },
      } = await getClientBirthdayReportAPI(query, storeId);
      dispatch(setIsClientBirthdayReportLoading(false));
      return data;
    } catch (error) {
      dispatch(setIsClientBirthdayReportLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};
